import { Box, Button, Tooltip } from '@mui/material';
import { Close, FilterAltOutlined } from '@mui/icons-material';
import { FilterContext, useFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { paloActiveFilters, paloWithoutFilters } from './palo-filter-keys';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PropsWithChildren, ReactElement, ReactNode, useEffect, useState } from 'react';
import { noop } from '@synergy/utils';

interface PaloFilterModalProps {
    title?: ReactNode;
}

/**
 * PALO filter modal: a PALO trigger button that opens the existing legacy filter
 * modal. Staging (apply/reset on commit) and the child filters are unchanged.
 */
export const PaloFilterModal = ({ title, children }: PropsWithChildren<PaloFilterModalProps>): ReactElement => {
    const { filter, reset, updateFilter, ...parentFilterContext } = useFilter();
    const [nextFilter, setNextFilter] = useState<URLSearchParams>(new URLSearchParams());
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setNextFilter(filter);
    }, [filter]);

    const activeCount = paloActiveFilters(filter).length;

    const onCommit = (): void => {
        updateFilter(nextFilter);
        setIsOpen(false);
    };

    const onReset = (): void => {
        reset();
        setIsOpen(false);
    };

    const actionChildren = [
        <Button key="1" variant="text" color="inherit" onClick={onReset} sx={{ color: tokens.color.ink2 }}>
            <FormattedMessage id="app.common.reset" />
        </Button>,
        <Button key="2" variant="contained" color="primary" onClick={onCommit}>
            <FormattedMessage id="app.common.filter" />
        </Button>,
    ];

    return (
        <>
            <PaloThemeProvider>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<FilterAltOutlined />}
                        onClick={() => setIsOpen(true)}
                        sx={{ color: activeCount > 0 ? tokens.color.accent : tokens.color.ink2 }}
                    >
                        <FormattedMessage id="app.common.filter" />
                        {/* How many are on, where the button that sets them is
                            — the chips below say which, but they are out of
                            sight once the list is scrolled (#2209). */}
                        {activeCount > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    ml: '6px',
                                    minWidth: 17,
                                    height: 17,
                                    px: '4px',
                                    borderRadius: '9px',
                                    backgroundColor: tokens.color.accent,
                                    color: '#fff',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    lineHeight: '17px',
                                    textAlign: 'center',
                                }}
                            >
                                {activeCount}
                            </Box>
                        )}
                    </Button>
                    {activeCount > 0 && (
                        <Tooltip title={<FormattedMessage id="app.filter.reset" />}>
                            <Box
                                role="button"
                                aria-label="Filter leeren"
                                onClick={() => updateFilter(paloWithoutFilters(filter))}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    padding: '3px',
                                    color: tokens.color.ink3,
                                    '&:hover': { color: tokens.color.ink, backgroundColor: tokens.color.canvas },
                                    '& svg': { fontSize: 16 },
                                }}
                            >
                                <Close />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </PaloThemeProvider>
            <PaloModal
                isVisible={isOpen}
                title={title ?? <FormattedMessage id="app.filter.filterTitle" />}
                handleClose={() => setIsOpen(false)}
                actionChildren={actionChildren}
                themeContent
            >
                <FilterContext.Provider
                    value={{
                        ...parentFilterContext,
                        filter: nextFilter,
                        reset: noop,
                        updateFilter: updated => setNextFilter(new URLSearchParams(updated)),
                    }}
                >
                    {children}
                </FilterContext.Provider>
            </PaloModal>
        </>
    );
};
